/**
 * ControlRoomPage Component
 * AIDLC Administrative Backend
 *
 * Module 03: Control Room
 * Real-time agent monitoring, session management, and prompt queue overview.
 * This is the operational command center for managing agent activity.
 *
 * Features:
 * - Agent status grid with real-time indicators
 * - Active sessions panel
 * - Prompt queue with status management
 * - Quick agent status controls
 *
 * A queued prompt quotes the conversation it came from, so the queue is shown to Admins and Operators only
 * (CHAT-READ-2): anyone else is told so where the queue would be, and the queue is not requested.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgents } from '../../hooks/useAgents';
import { useBridge } from '../layout/MainLayout';
import { useAuth } from '../../hooks/useAuth';
import { Badge } from '../shared/Badge';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import type { Agent, AgentStatus } from '../../types/agent';
import type { PromptQueueItem } from '../../types/chat';
import { getPromptQueue, updatePromptStatus, editPrompt, cancelPrompt } from '../../services/chatService';
import { Modal } from '../shared/Modal';
import { Button } from '../shared/Button';
import { updateAgentStatus, getAllActiveTasks, killAgentSession } from '../../services/agentService';
import type { ActiveTask } from '../../services/agentService';

const statusColors: Record<string, { bg: string; text: string; dot: string }> = {
  working: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  available: { bg: 'bg-blue-50', text: 'text-ldaja-blue', dot: 'bg-ldaja-blue' },
  waiting: { bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  away: { bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-400' },
};

/** What someone who may not read the prompt queue is told. The server refuses them the queue as well. */
export const PROMPT_QUEUE_NOT_AVAILABLE_NOTE = 'The prompt queue is available to Admins and Operators.';

export function ControlRoomPage() {
  const { agents, isLoading: agentsLoading, refresh: refreshAgents } = useAgents(10000); // 10s polling
  const { socket } = useBridge();
  const { isAdmin, isOperator } = useAuth();
  const canReadQueue = isAdmin || isOperator;
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState<PromptQueueItem[]>([]);
  const [activeTasks, setActiveTasks] = useState<Map<string, ActiveTask>>(new Map());
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<'agents' | 'prompts'>('agents');

  // Edit prompt modal state
  const [editingPrompt, setEditingPrompt] = useState<PromptQueueItem | null>(null);
  const [editSubject, setEditSubject] = useState('');
  const [editPromptText, setEditPromptText] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Load prompts + active tasks
  useEffect(() => {
    async function load() {
      try {
        const [queue, taskData] = await Promise.all([
          canReadQueue ? getPromptQueue() : Promise.resolve([]),
          getAllActiveTasks(),
        ]);
        setPrompts(queue);
        const taskMap = new Map<string, ActiveTask>();
        taskData.tasks.forEach((t) => taskMap.set(t.agentId, t));
        setActiveTasks(taskMap);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load control room data');
      } finally {
        setIsLoadingPrompts(false);
      }
    }
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [canReadQueue]);

  // Real-time agent status updates via WebSocket
  useEffect(() => {
    if (!socket) return;
    const handleStatus = (data: { agentId: string; status: string; activeTask?: ActiveTask | null }) => {
      if (!data) return;
      setActiveTasks((prev) => {
        const next = new Map(prev);
        if (data.activeTask) {
          next.set(data.agentId, data.activeTask);
        } else if (data.status !== 'working') {
          next.delete(data.agentId);
        }
        return next;
      });
      refreshAgents();
    };
    socket.on('agent:status', handleStatus);
    return () => { socket.off('agent:status', handleStatus); };
  }, [socket, refreshAgents]);

  const agentsByStatus = useMemo(() => {
    const groups: Record<AgentStatus, Agent[]> = {
      working: [],
      available: [],
      waiting: [],
      away: [],
    };
    agents.forEach((a) => {
      if (groups[a.status]) groups[a.status].push(a);
    });
    return groups;
  }, [agents]);

  const pendingPrompts = useMemo(() => prompts.filter((p) => p.status === 'pending'), [prompts]);
  const processingPrompts = useMemo(() => prompts.filter((p) => p.status === 'processing'), [prompts]);

  const handleSetAgentStatus = async (agentId: string, status: AgentStatus) => {
    try {
      await updateAgentStatus(agentId, { status });
      refreshAgents();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update agent status');
    }
  };

  const handleKillSession = async (agentId: string) => {
    try {
      await killAgentSession(agentId);
      setActiveTasks((prev) => {
        const next = new Map(prev);
        next.delete(agentId);
        return next;
      });
      refreshAgents();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to kill session');
    }
  };

  const handlePromptAction = async (promptId: string, status: string) => {
    try {
      const updated = await updatePromptStatus(promptId, status);
      setPrompts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update prompt');
    }
  };

  const openEditPrompt = (prompt: PromptQueueItem) => {
    setEditingPrompt(prompt);
    setEditSubject(prompt.subject);
    setEditPromptText(prompt.prompt);
  };

  const handleSaveEdit = async () => {
    if (!editingPrompt) return;
    setIsSavingEdit(true);
    try {
      const updated = await editPrompt(editingPrompt.id, {
        subject: editSubject,
        prompt: editPromptText,
      });
      setPrompts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setEditingPrompt(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to edit prompt');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleCancelPrompt = async (promptId: string) => {
    try {
      await cancelPrompt(promptId);
      setPrompts((prev) => prev.filter((p) => p.id !== promptId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel prompt');
    }
  };

  const formatTime = (ts: string) => {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (agentsLoading) return <LoadingSpinner message="Loading control room..." />;

  return (
    <div>
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-ldaja-black">{agents.length}</div>
          <div className="text-[10px] text-gray-500">Total Agents</div>
        </div>
        <div className="bg-white border border-green-200 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-green-600">{agentsByStatus.working.length}</div>
          <div className="text-[10px] text-gray-500">Working</div>
        </div>
        <div className="bg-white border border-blue-200 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-ldaja-blue">{agentsByStatus.available.length}</div>
          <div className="text-[10px] text-gray-500">Available</div>
        </div>
        <div className="bg-white border border-yellow-200 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-yellow-600">{agentsByStatus.waiting.length}</div>
          <div className="text-[10px] text-gray-500">Waiting</div>
        </div>
        {canReadQueue && (
          <>
            <div className="bg-white border border-orange-200 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-orange-600">{pendingPrompts.length}</div>
              <div className="text-[10px] text-gray-500">Pending Prompts</div>
            </div>
            <div className="bg-white border border-blue-200 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-ldaja-blue">{processingPrompts.length}</div>
              <div className="text-[10px] text-gray-500">Processing</div>
            </div>
          </>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-700 text-xs">{error}</p>
          <button onClick={() => setError(null)} className="text-red-600 text-xs underline mt-1">Dismiss</button>
        </div>
      )}

      {/* Tab Switcher */}
      <div className="flex items-center gap-1 mb-6 border-b border-gray-200">
        {(['agents', 'prompts'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setSelectedTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              selectedTab === tab
                ? 'border-ldaja-blue text-ldaja-blue'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'agents' ? 'Agent Status Grid' : canReadQueue ? `Prompt Queue (${prompts.length})` : 'Prompt Queue'}
          </button>
        ))}
      </div>

      {/* Agent Status Grid */}
      {selectedTab === 'agents' && (
        <div className="space-y-6">
          {/* Working Agents -- Always shown first, highlighted */}
          {agentsByStatus.working.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                Working ({agentsByStatus.working.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {agentsByStatus.working.map((agent) => (
                  <AgentControlCard
                    key={agent.id}
                    agent={agent}
                    activeTask={activeTasks.get(agent.id) || null}
                    onSetStatus={handleSetAgentStatus}
                    onChat={() => navigate(`/chat?agent=${agent.id}`)}
                    onViewProfile={() => navigate(`/agents/${agent.id}`)}
                    onKillSession={() => handleKillSession(agent.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Available Agents */}
          {agentsByStatus.available.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-ldaja-blue uppercase tracking-wider mb-3">
                Available ({agentsByStatus.available.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {agentsByStatus.available.map((agent) => (
                  <AgentControlCard
                    key={agent.id}
                    agent={agent}
                    activeTask={null}
                    onSetStatus={handleSetAgentStatus}
                    onChat={() => navigate(`/chat?agent=${agent.id}`)}
                    onViewProfile={() => navigate(`/agents/${agent.id}`)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Waiting Agents */}
          {agentsByStatus.waiting.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-yellow-700 uppercase tracking-wider mb-3">
                Waiting ({agentsByStatus.waiting.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {agentsByStatus.waiting.map((agent) => (
                  <AgentControlCard
                    key={agent.id}
                    agent={agent}
                    activeTask={activeTasks.get(agent.id) || null}
                    onSetStatus={handleSetAgentStatus}
                    onChat={() => navigate(`/chat?agent=${agent.id}`)}
                    onViewProfile={() => navigate(`/agents/${agent.id}`)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Away Agents */}
          {agentsByStatus.away.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Away ({agentsByStatus.away.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {agentsByStatus.away.map((agent) => (
                  <AgentControlCard
                    key={agent.id}
                    agent={agent}
                    activeTask={null}
                    onSetStatus={handleSetAgentStatus}
                    onChat={() => navigate(`/chat?agent=${agent.id}`)}
                    onViewProfile={() => navigate(`/agents/${agent.id}`)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Prompt Queue -- Grouped by Agent */}
      {selectedTab === 'prompts' && (
        <div>
          {!canReadQueue ? (
            <div className="bg-white rounded-xl border border-gray-200" data-testid="prompt-queue-not-available">
              <EmptyState title="Prompt queue" description={PROMPT_QUEUE_NOT_AVAILABLE_NOTE} />
            </div>
          ) : isLoadingPrompts ? (
            <LoadingSpinner message="Loading prompt queue..." />
          ) : prompts.length === 0 ? (
            <EmptyState
              title="Prompt queue is empty"
              description="No prompts have been queued. Use the Chat Interface to create prompts with the Go Do It button."
            />
          ) : (
            <div className="space-y-4">
              {/* Group prompts by agent */}
              {(() => {
                const byAgent: Record<string, PromptQueueItem[]> = {};
                prompts.forEach((p) => {
                  const key = p.toAgent || 'unassigned';
                  if (!byAgent[key]) byAgent[key] = [];
                  byAgent[key].push(p);
                });
                return Object.entries(byAgent).map(([agentId, agentPrompts]) => {
                  const agent = agents.find((a) => a.id === agentId);
                  const pendingCount = agentPrompts.filter((p) => p.status === 'pending').length;
                  return (
                    <div key={agentId} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      {/* Agent Header */}
                      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
                        {agent && (
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                            style={{ backgroundColor: agent.avatarColor || '#2563EB' }}
                          >
                            {agent.name.charAt(0)}
                          </div>
                        )}
                        <span className="text-xs font-semibold text-ldaja-black">
                          {agent?.name || agentId}
                        </span>
                        <span className="text-[10px] text-gray-500">{agent?.role || ''}</span>
                        <div className="ml-auto flex items-center gap-2">
                          {pendingCount > 0 && (
                            <Badge variant="warning" dot>{pendingCount} pending</Badge>
                          )}
                          <Badge variant="neutral">{agentPrompts.length} total</Badge>
                        </div>
                      </div>
                      {/* Prompts Table */}
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-100">
                            <th className="text-left px-4 py-1.5 text-[10px] font-semibold text-gray-500 uppercase">Subject</th>
                            <th className="text-left px-4 py-1.5 text-[10px] font-semibold text-gray-500 uppercase">Priority</th>
                            <th className="text-left px-4 py-1.5 text-[10px] font-semibold text-gray-500 uppercase">Status</th>
                            <th className="text-left px-4 py-1.5 text-[10px] font-semibold text-gray-500 uppercase">Created</th>
                            <th className="text-left px-4 py-1.5 text-[10px] font-semibold text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {agentPrompts.map((prompt) => (
                            <tr key={prompt.id} className="hover:bg-gray-50">
                              <td className="px-4 py-2.5 text-xs font-medium text-ldaja-black max-w-[250px] truncate">
                                {prompt.subject}
                              </td>
                              <td className="px-4 py-2.5">
                                <Badge
                                  variant={
                                    prompt.priority === 'urgent' ? 'danger' :
                                    prompt.priority === 'high' ? 'warning' :
                                    prompt.priority === 'normal' ? 'default' : 'neutral'
                                  }
                                >
                                  {prompt.priority}
                                </Badge>
                              </td>
                              <td className="px-4 py-2.5">
                                <Badge
                                  variant={
                                    prompt.status === 'pending' ? 'warning' :
                                    prompt.status === 'processing' ? 'info' :
                                    prompt.status === 'completed' ? 'success' : 'danger'
                                  }
                                  dot
                                >
                                  {prompt.status}
                                </Badge>
                              </td>
                              <td className="px-4 py-2.5 text-xs text-gray-500">
                                {formatTime(prompt.createdAt)}
                              </td>
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-1">
                                  {prompt.status === 'pending' && (
                                    <>
                                      <button
                                        onClick={() => handlePromptAction(prompt.id, 'processing')}
                                        className="text-[10px] px-2 py-1 bg-ldaja-blue text-white rounded hover:bg-blue-700 transition-colors"
                                      >
                                        Start
                                      </button>
                                      <button
                                        onClick={() => openEditPrompt(prompt)}
                                        className="text-[10px] px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => handleCancelPrompt(prompt.id)}
                                        className="text-[10px] px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
                                      >
                                        Cancel
                                      </button>
                                    </>
                                  )}
                                  {prompt.status === 'processing' && (
                                    <button
                                      onClick={() => handlePromptAction(prompt.id, 'completed')}
                                      className="text-[10px] px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                                    >
                                      Complete
                                    </button>
                                  )}
                                  {prompt.status === 'processing' && (
                                    <button
                                      onClick={() => handlePromptAction(prompt.id, 'failed')}
                                      className="text-[10px] px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                                    >
                                      Fail
                                    </button>
                                  )}
                                  {prompt.status === 'failed' && (
                                    <button
                                      onClick={() => handleCancelPrompt(prompt.id)}
                                      className="text-[10px] px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      )}
      {/* Edit Prompt Modal */}
      <Modal
        isOpen={!!editingPrompt}
        onClose={() => setEditingPrompt(null)}
        title="Edit Prompt"
        size="md"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setEditingPrompt(null)}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleSaveEdit} isLoading={isSavingEdit}>Save Changes</Button>
          </>
        }
      >
        {editingPrompt && (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
              <input
                type="text"
                value={editSubject}
                onChange={(e) => setEditSubject(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Prompt</label>
              <textarea
                value={editPromptText}
                onChange={(e) => setEditPromptText(e.target.value)}
                rows={10}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 font-mono"
              />
            </div>
            <div className="text-[10px] text-gray-400">
              Assigned to: {agents.find((a) => a.id === editingPrompt.toAgent)?.name || editingPrompt.toAgent} | Priority: {editingPrompt.priority}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ============================================================
// Agent Control Card Sub-component
// ============================================================

function AgentControlCard({
  agent,
  activeTask,
  onSetStatus,
  onChat,
  onViewProfile,
  onKillSession,
}: {
  agent: Agent;
  activeTask: ActiveTask | null;
  onSetStatus: (agentId: string, status: AgentStatus) => void;
  onChat: () => void;
  onViewProfile: () => void;
  onKillSession?: () => void;
}) {
  const colors = statusColors[agent.status] ?? statusColors.away;
  const isWorking = agent.status === 'working';

  // Elapsed time for active tasks
  const elapsed = activeTask
    ? Math.floor((Date.now() - new Date(activeTask.startedAt).getTime()) / 1000)
    : 0;
  const elapsedStr = elapsed > 0
    ? elapsed >= 3600
      ? `${Math.floor(elapsed / 3600)}h ${Math.floor((elapsed % 3600) / 60)}m`
      : elapsed >= 60
        ? `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`
        : `${elapsed}s`
    : '';

  return (
    <div className={`bg-white border rounded-lg p-3 ${isWorking ? 'border-green-300 ring-1 ring-green-100' : 'border-gray-200'}`}>
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
          style={{ backgroundColor: agent.avatarColor || '#2563EB' }}
        >
          {agent.name.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-xs font-semibold text-ldaja-black truncate">{agent.name}</h4>
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${colors.bg} ${colors.text}`}>
              <span className={`w-1 h-1 rounded-full ${colors.dot} ${isWorking ? 'animate-pulse' : ''}`} />
              {agent.status}
            </span>
          </div>
          <p className="text-[10px] text-gray-500 truncate">{agent.role}</p>
        </div>
      </div>

      {/* Active task info */}
      {activeTask ? (
        <div className="bg-green-50 border border-green-100 rounded px-2 py-1.5 mb-2">
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-green-800 font-medium truncate flex-1">{activeTask.subject}</p>
            <span className="text-[9px] text-green-600 font-mono ml-2 flex-shrink-0">{elapsedStr}</span>
          </div>
          <p className="text-[9px] text-green-600 mt-0.5">
            via {activeTask.source === 'chat' ? 'Chat' : activeTask.source === 'go-do-it' ? 'Go Do It' : activeTask.source}
          </p>
        </div>
      ) : agent.statusTask ? (
        <p className="text-[10px] text-gray-600 bg-gray-50 rounded px-2 py-1 mb-2 truncate">
          {agent.statusTask}
        </p>
      ) : null}

      {/* Quick Actions */}
      <div className="flex items-center gap-1.5 mb-2">
        <button
          onClick={onChat}
          className="flex-1 flex items-center justify-center gap-1 text-[10px] py-1.5 rounded bg-ldaja-blue/10 text-ldaja-blue hover:bg-ldaja-blue/20 transition-colors"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          Chat
        </button>
        <button
          onClick={onViewProfile}
          className="flex-1 flex items-center justify-center gap-1 text-[10px] py-1.5 rounded bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Profile
        </button>
        {isWorking && onKillSession && (
          <button
            onClick={onKillSession}
            className="flex items-center justify-center gap-1 text-[10px] py-1.5 px-2 rounded bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
            title="Kill active session"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
            Kill
          </button>
        )}
      </div>

      {/* Quick Status Controls */}
      <div className="flex items-center gap-1">
        {(['working', 'available', 'waiting', 'away'] as AgentStatus[]).map((s) => (
          <button
            key={s}
            onClick={() => onSetStatus(agent.id, s)}
            disabled={agent.status === s}
            className={`flex-1 text-[9px] py-1 rounded transition-colors ${
              agent.status === s
                ? `${statusColors[s].bg} ${statusColors[s].text} font-medium`
                : 'bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>
    </div>
  );
}
